/**
 * Files Module - Mini-Dropbox File Manager
 *
 * Hierarchical file storage with S3 backend.
 * Features: Tree navigation, grid/list views, upload, folders, search.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Outlet } from 'react-router-dom';
import {
  FileNode,
  TreeNode,
  FilesViewMode,
  FilesSortBy,
  FilesSortOrder,
  BreadcrumbItem,
  getFileTypeConfig,
  formatFileDate,
  sortFiles,
  FILE_TYPE_CONFIG,
  isSystemFolder,
} from '@/types/files';
import FileProcessingBadge, { isProcessableType } from '@/components/files/FileProcessingBadge';
import {
  listFolder,
  getFileTree,
  searchFiles,
  getRecentFiles,
  createFolder,
  uploadFilesWithProgress,
  uploadDirectory,
  updateFile,
  deleteFile,
  downloadFile,
  downloadZip,
  getBreadcrumb,
  getPreviewUrl,
  cancelUpload,
  formatFileSize,
  UploadProgressState,
} from '@/services/filesApi';
import MoveDialog from '@/components/files/MoveDialog';
import CopyDialog from '@/components/files/CopyDialog';
import TaskFolderBanner from '@/components/files/TaskFolderBanner';
import { useTranslation } from 'react-i18next';

// ============================================================================
// Main Component
// ============================================================================

const FilesModule: React.FC = () => {
  const { t } = useTranslation('files');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Folder ID from URL - null means root
  const currentFolderId = searchParams.get('folder');

  // Navigation state
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: t('breadcrumbs.root') }]);

  // Files state
  const [files, setFiles] = useState<FileNode[]>([]);
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [recentFiles, setRecentFiles] = useState<FileNode[]>([]);

  // UI state
  const [viewMode, setViewMode] = useState<FilesViewMode>('grid');
  const [sortBy, setSortBy] = useState<FilesSortBy>('name');
  const [sortOrder, setSortOrder] = useState<FilesSortOrder>('asc');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FileNode[] | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [editingFile, setEditingFile] = useState<FileNode | null>(null);
  const [filesToMove, setFilesToMove] = useState<FileNode[]>([]);
  const [filesToCopy, setFilesToCopy] = useState<FileNode[]>([]);

  // Task folder context
  const [taskContext, setTaskContext] = useState<{ taskId: string; taskTitle?: string } | null>(null);

  // Form state
  const [newFolderName, setNewFolderName] = useState('');
  const [newFileName, setNewFileName] = useState('');

  // Upload state
  const [uploadProgress, setUploadProgress] = useState<UploadProgressState | null>(null);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const directoryInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortController = useRef<AbortController | null>(null);

  // ============================================================================
  // Data Loading
  // ============================================================================

  const loadFolder = useCallback(async (folderId: string | null) => {
    setLoading(true);
    try {
      const [folderFiles, treeData] = await Promise.all([
        listFolder(folderId, true),
        getFileTree(null, 3, false),  // Get folder tree only
      ]);
      setFiles(sortFiles(folderFiles, sortBy, sortOrder));
      setTree(treeData);
      setSearchResults(null);
      setSelectedIds(new Set());

      // Update breadcrumbs
      if (folderId) {
        try {
          const pathFiles = await getBreadcrumb(folderId);
          setBreadcrumbs([
            { id: null, name: t('breadcrumbs.root') },
            ...pathFiles.map(f => ({ id: f.id, name: f.name }))
          ]);
          // Detect task folder context from breadcrumb path
          const taskFolder = pathFiles.find(f => f.folder_type === 'task');
          if (taskFolder && taskFolder.path) {
            const match = taskFolder.path.match(/^\/tasks\/([A-Z0-9]+)$/i);
            if (match) {
              setTaskContext({ taskId: match[1], taskTitle: taskFolder.name });
            } else {
              setTaskContext(null);
            }
          } else {
            setTaskContext(null);
          }
        } catch {
          setBreadcrumbs([{ id: null, name: t('breadcrumbs.root') }]);
          setTaskContext(null);
        }
      } else {
        setBreadcrumbs([{ id: null, name: t('breadcrumbs.root') }]);
        setTaskContext(null);
      }
    } catch (error) {
      console.error('Failed to load folder:', error);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  const loadRecentFiles = useCallback(async () => {
    try {
      const recent = await getRecentFiles(5);
      setRecentFiles(recent);
    } catch (error) {
      console.error('Failed to load recent files:', error);
    }
  }, []);

  useEffect(() => {
    loadFolder(currentFolderId);
    loadRecentFiles();
  }, [currentFolderId, loadFolder, loadRecentFiles]);

  // ============================================================================
  // Search
  // ============================================================================

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    setLoading(true);
    try {
      const results = await searchFiles({ query, limit: 50 });
      setSearchResults(results.files);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  // Close upload menu when clicking outside
  useEffect(() => {
    if (!showUploadMenu) return;

    const handleClickOutside = () => setShowUploadMenu(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUploadMenu]);

  // ============================================================================
  // Navigation
  // ============================================================================

  const navigateToFolder = (folderId: string | null) => {
    if (folderId) {
      setSearchParams({ folder: folderId });
    } else {
      setSearchParams({});
    }
    setSearchQuery('');
  };

  const handleFileClick = (file: FileNode) => {
    if (file.is_folder) {
      navigateToFolder(file.id);
    } else {
      // Navigate to file preview with file info in state for faster loading
      const previewUrl = currentFolderId
        ? `/disk/${file.id}/preview?folder=${currentFolderId}`
        : `/disk/${file.id}/preview`;
      navigate(previewUrl, {
        state: {
          fileName: file.name,
          fileSize: file.file_size || 0,
        },
      });
    }
  };

  // ============================================================================
  // Selection
  // ============================================================================

  const toggleSelection = (fileId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newSelection = new Set(selectedIds);

    // Check if clicked on a checkbox (always toggle) or elsewhere
    const target = event.target as HTMLElement;
    const isCheckboxClick = target.tagName === 'INPUT' || target.closest('td')?.querySelector('input[type="checkbox"]');

    if (event.shiftKey && selectedIds.size > 0) {
      // Range selection - not implemented for simplicity
    } else if (event.ctrlKey || event.metaKey || isCheckboxClick) {
      // Toggle - also triggered by checkbox clicks
      if (newSelection.has(fileId)) {
        newSelection.delete(fileId);
      } else {
        newSelection.add(fileId);
      }
    } else {
      // Single selection (only when clicking row, not checkbox)
      newSelection.clear();
      newSelection.add(fileId);
    }
    setSelectedIds(newSelection);
  };

  // ============================================================================
  // File Operations
  // ============================================================================

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    try {
      await createFolder({
        name: newFolderName.trim(),
        parent_id: currentFolderId,
      });
      setNewFolderName('');
      setShowCreateFolderModal(false);
      await loadFolder(currentFolderId);
    } catch (error) {
      console.error('Failed to create folder:', error);
      alert(t('errors.create_folder_failed'));
    }
  };

  const handleUpload = async (filesToUpload: FileList | File[]) => {
    const fileArray = Array.from(filesToUpload);
    if (fileArray.length === 0) return;

    // Create abort controller for cancellation
    uploadAbortController.current = new AbortController();

    try {
      await uploadFilesWithProgress(
        fileArray,
        currentFolderId,
        (progress) => setUploadProgress(progress),
        {
          maxConcurrent: 3,
          abortController: uploadAbortController.current
        }
      );
      await loadFolder(currentFolderId);
      await loadRecentFiles();
    } catch (error) {
      // Only show error if not cancelled
      const axiosError = error as { code?: string };
      if (axiosError.code !== 'ERR_CANCELED') {
        console.error('Upload failed:', error);
        // Don't show alert - the UI shows individual file errors
      }
    } finally {
      setUploadProgress(null);
      uploadAbortController.current = null;
    }
  };

  const handleCancelUpload = () => {
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
    }
    // Clean up any pending uploads
    if (uploadProgress) {
      uploadProgress.files
        .filter(f => f.status === 'pending' || f.status === 'uploading')
        .forEach(f => {
          if (f.fileId && !f.fileId.startsWith('temp-')) {
            cancelUpload(f.fileId).catch(() => {});
          }
        });
    }
    setUploadProgress(null);
    uploadAbortController.current = null;
  };

  const handleDirectoryUpload = async (filesToUpload: FileList | null) => {
    if (!filesToUpload || filesToUpload.length === 0) return;

    // Directory upload still uses the batch endpoint to preserve folder structure
    // Show a simple progress indicator
    const fileArray = Array.from(filesToUpload);
    setUploadProgress({
      files: fileArray.map((f, i) => ({
        fileId: `dir-${i}`,
        fileName: f.name,
        fileSize: f.size,
        bytesUploaded: 0,
        percentage: 0,
        uploadSpeed: 0,
        estimatedTimeRemaining: 0,
        status: 'pending' as const
      })),
      totalBytes: fileArray.reduce((sum, f) => sum + f.size, 0),
      uploadedBytes: 0,
      overallPercentage: 0,
      completedCount: 0,
      failedCount: 0,
      totalCount: fileArray.length
    });

    try {
      const result = await uploadDirectory(filesToUpload, currentFolderId);

      // Update progress to show completion
      setUploadProgress(prev => prev ? {
        ...prev,
        completedCount: result.total_uploaded,
        failedCount: result.total_failed,
        overallPercentage: 100,
        uploadedBytes: prev.totalBytes
      } : null);

      if (result.errors.length > 0) {
        console.warn('Some files failed to upload:', result.errors);
      }

      await loadFolder(currentFolderId);
      await loadRecentFiles();
    } catch (error) {
      console.error('Directory upload failed:', error);
    } finally {
      // Keep progress visible briefly to show completion
      setTimeout(() => setUploadProgress(null), 2000);
    }
  };

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFile || !newFileName.trim()) return;

    try {
      await updateFile(editingFile.id, { name: newFileName.trim() });
      setEditingFile(null);
      setNewFileName('');
      setShowRenameModal(false);
      await loadFolder(currentFolderId);
    } catch (error) {
      console.error('Failed to rename:', error);
      alert(t('errors.rename_failed'));
    }
  };

  const handleDelete = async (file: FileNode) => {
    // Prevent deletion of system folders
    if (isSystemFolder(file)) {
      alert(t('confirmations.system_folder_no_delete'));
      return;
    }

    const confirmMsg = file.is_folder
      ? t('confirmations.delete_folder', { name: file.name })
      : t('confirmations.delete_file', { name: file.name });

    if (!confirm(confirmMsg)) return;

    try {
      await deleteFile(file.id);
      await loadFolder(currentFolderId);
    } catch (error) {
      console.error('Failed to delete:', error);
      // Check if error is about protected folders
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete';
      if (errorMsg.toLowerCase().includes('protected') || errorMsg.toLowerCase().includes('system folder')) {
        alert(t('confirmations.delete_protected'));
      } else {
        alert(errorMsg);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(t('confirmations.delete_items', { count: selectedIds.size }))) return;

    try {
      for (const id of selectedIds) {
        await deleteFile(id);
      }
      setSelectedIds(new Set());
      await loadFolder(currentFolderId);
    } catch (error) {
      console.error('Failed to delete:', error);
      alert(t('errors.some_delete_failed'));
    }
  };

  const handleDownloadZip = async () => {
    if (selectedIds.size === 0) return;
    try {
      await downloadZip(Array.from(selectedIds));
    } catch (error) {
      console.error('Failed to download ZIP:', error);
      alert(t('errors.download_zip_failed'));
    }
  };

  const handleDownload = async (file: FileNode) => {
    try {
      await downloadFile(file.id, file.name);
    } catch (error) {
      console.error('Download failed:', error);
      alert(t('errors.download_failed'));
    }
  };

  const openRenameModal = (file: FileNode) => {
    setEditingFile(file);
    setNewFileName(file.name);
    setShowRenameModal(true);
  };

  const openMoveDialog = (file: FileNode) => {
    setFilesToMove([file]);
    setShowMoveDialog(true);
  };

  const openCopyDialog = (file: FileNode) => {
    setFilesToCopy([file]);
    setShowCopyDialog(true);
  };

  const handleMoveComplete = () => {
    loadFolder(currentFolderId);
    setSelectedIds(new Set());
  };

  const handleCopyComplete = () => {
    loadFolder(currentFolderId);
  };

  // ============================================================================
  // Drag and Drop
  // ============================================================================

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      await handleUpload(droppedFiles);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  // For search results, preserve backend relevance order; for regular listing, apply user sort
  const displayFiles = searchResults !== null ? searchResults : files;
  const sortedFiles = searchResults !== null ? displayFiles : sortFiles(displayFiles, sortBy, sortOrder);

  return (
    <div
      className="bg-gray-50"
      style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Header - uses CSS variable from HealthBanner */}
      <div className="bg-white border-b border-gray-200 sticky z-30" style={{ top: 'var(--health-banner-height, 0px)' }}>
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{t('header.title')}</h1>
                <p className="text-xs md:text-sm text-gray-500">
                  {files.filter(f => f.is_folder).length} {t('header.subtitle_folders')}, {files.filter(f => !f.is_folder).length} {t('header.subtitle_files')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateFolderModal(true)}
                className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
                {t('header.button_new_folder')}
              </button>
              {/* Upload dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUploadMenu(!showUploadMenu);
                  }}
                  className="btn-primary text-sm px-3 md:px-4 py-2 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="hidden sm:inline">{t('header.button_upload')}</span>
                  <svg className="w-3 h-3 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showUploadMenu && (
                  <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]">
                    <button
                      onClick={() => {
                        fileInputRef.current?.click();
                        setShowUploadMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('upload.menu_files')}
                    </button>
                    <button
                      onClick={() => {
                        directoryInputRef.current?.click();
                        setShowUploadMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      {t('upload.menu_folder')}
                    </button>
                  </div>
                )}
              </div>

              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  e.target.files && handleUpload(e.target.files);
                  e.target.value = ''; // Reset to allow re-upload of same files
                }}
              />
              <input
                ref={directoryInputRef}
                type="file"
                {...{ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>}
                multiple
                className="hidden"
                onChange={(e) => {
                  handleDirectoryUpload(e.target.files);
                  e.target.value = ''; // Reset
                }}
              />
            </div>
          </div>

          {/* Breadcrumbs */}
          <div className="mt-3 flex items-center gap-1 text-sm overflow-x-auto">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id ?? 'root'}>
                {index > 0 && (
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
                <button
                  onClick={() => navigateToFolder(crumb.id)}
                  className={`px-2 py-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0 ${
                    index === breadcrumbs.length - 1 ? 'font-medium text-gray-900' : 'text-gray-600'
                  }`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Task Folder Banner */}
      {taskContext && (
        <div className="px-4 md:px-6 pt-2">
          <TaskFolderBanner taskId={taskContext.taskId} taskTitle={taskContext.taskTitle} />
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-3">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          {/* Search */}
          <div className="flex-1 max-w-md relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('toolbar.search_placeholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title={t('toolbar.view_grid')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title={t('toolbar.view_list')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as FilesSortBy)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="name">{t('toolbar.sort_name')}</option>
              <option value="modified">{t('toolbar.sort_modified')}</option>
              <option value="size">{t('toolbar.sort_size')}</option>
              <option value="type">{t('toolbar.sort_type')}</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              title={sortOrder === 'asc' ? t('toolbar.sort_ascending') : t('toolbar.sort_descending')}
            >
              <svg
                className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>

            {/* Bulk Actions */}
            {selectedIds.size > 0 && (
              <>
                <button
                  onClick={handleDownloadZip}
                  className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t('toolbar.zip_button')} ({selectedIds.size})
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg"
                >
                  {t('toolbar.delete_button')} ({selectedIds.size})
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex">
        {/* Sidebar - Tree View */}
        <div className="hidden lg:block w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-180px)]">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">{t('sidebar.folders_label')}</h3>
            <TreeView
              nodes={tree}
              currentFolderId={currentFolderId}
              onNavigate={navigateToFolder}
            />

            {/* Recent Files */}
            {recentFiles.length > 0 && (
              <>
                <h3 className="text-xs font-semibold text-gray-500 uppercase mt-6 mb-3">{t('sidebar.recent_label')}</h3>
                <div className="space-y-1">
                  {recentFiles.map(file => (
                    <button
                      key={file.id}
                      onClick={() => handleFileClick(file)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg text-left"
                    >
                      <span className="text-base">{getFileTypeConfig(file).icon}</span>
                      <span className="truncate">{file.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Files Area */}
        <div className="flex-1 p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : sortedFiles.length === 0 ? (
            <EmptyState
              isSearch={searchQuery.length > 0}
              onCreateFolder={() => setShowCreateFolderModal(true)}
              onUpload={() => fileInputRef.current?.click()}
            />
          ) : viewMode === 'grid' ? (
            <GridView
              files={sortedFiles}
              selectedIds={selectedIds}
              onFileClick={handleFileClick}
              onSelect={toggleSelection}
              onRename={openRenameModal}
              onDelete={handleDelete}
              onDownload={handleDownload}
              onMove={openMoveDialog}
              onCopy={openCopyDialog}
            />
          ) : (
            <ListView
              files={sortedFiles}
              selectedIds={selectedIds}
              onFileClick={handleFileClick}
              onSelect={toggleSelection}
              onRename={openRenameModal}
              onDelete={handleDelete}
              onDownload={handleDownload}
              onMove={openMoveDialog}
              onCopy={openCopyDialog}
            />
          )}
        </div>
      </div>

      {/* Upload Progress - Enhanced with per-file progress, speed, and ETA */}
      {uploadProgress && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-96 max-h-96 overflow-hidden flex flex-col z-50">
          {/* Header */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">{t('upload.title_uploading')}</span>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {uploadProgress.completedCount}/{uploadProgress.totalCount}
              </span>
            </div>
            <button
              onClick={handleCancelUpload}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title={t('upload.cancel')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Overall Progress */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{formatFileSize(uploadProgress.uploadedBytes)} / {formatFileSize(uploadProgress.totalBytes)}</span>
              <span>{uploadProgress.overallPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.overallPercentage}%` }}
              />
            </div>
          </div>

          {/* Per-file Progress List */}
          <div className="flex-1 overflow-y-auto space-y-2 max-h-48">
            {uploadProgress.files.map((file) => (
              <div key={file.fileId} className="bg-gray-50 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 truncate max-w-[180px]" title={file.fileName}>
                    {file.fileName}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    file.status === 'completed' ? 'bg-green-100 text-green-700' :
                    file.status === 'failed' ? 'bg-red-100 text-red-700' :
                    file.status === 'uploading' ? 'bg-blue-100 text-blue-700' :
                    file.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {file.status === 'uploading' ? `${file.percentage}%` : file.status}
                  </span>
                </div>

                {file.status === 'uploading' && (
                  <>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-200"
                        style={{ width: `${file.percentage}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>
                        {file.uploadSpeed > 0 ? `${formatFileSize(file.uploadSpeed)}${t('upload.speed')}` : '...'}
                      </span>
                      <span>
                        {file.estimatedTimeRemaining > 0
                          ? file.estimatedTimeRemaining < 60
                            ? `${file.estimatedTimeRemaining}${t('upload.time_seconds')}`
                            : `${Math.round(file.estimatedTimeRemaining / 60)}${t('upload.time_minutes')}`
                          : '...'}
                      </span>
                    </div>
                  </>
                )}

                {file.status === 'failed' && file.error && (
                  <p className="text-xs text-red-500 mt-1 truncate" title={file.error}>
                    {file.error}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Failed count warning */}
          {uploadProgress.failedCount > 0 && (
            <div className="mt-2 pt-2 border-t text-xs text-red-600">
              {uploadProgress.failedCount} {t('upload.files_failed')}
            </div>
          )}
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <Modal title={t('modals.create_folder_title')} onClose={() => setShowCreateFolderModal(false)}>
          <form onSubmit={handleCreateFolder} className="p-6">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t('modals.create_folder_placeholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <button type="submit" className="btn-primary flex-1">{t('modals.create_folder_submit')}</button>
              <button type="button" onClick={() => setShowCreateFolderModal(false)} className="btn-secondary flex-1">{t('modals.create_folder_cancel')}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Rename Modal */}
      {showRenameModal && editingFile && (
        <Modal title={t('modals.rename_title')} onClose={() => setShowRenameModal(false)}>
          <form onSubmit={handleRename} className="p-6">
            <input
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              placeholder={t('modals.rename_placeholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <button type="submit" className="btn-primary flex-1">{t('modals.rename_submit')}</button>
              <button type="button" onClick={() => setShowRenameModal(false)} className="btn-secondary flex-1">{t('modals.rename_cancel')}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Move Dialog */}
      <MoveDialog
        isOpen={showMoveDialog}
        files={filesToMove}
        onClose={() => setShowMoveDialog(false)}
        onMoveComplete={handleMoveComplete}
      />

      {/* Copy Dialog */}
      <CopyDialog
        isOpen={showCopyDialog}
        files={filesToCopy}
        onClose={() => setShowCopyDialog(false)}
        onCopyComplete={handleCopyComplete}
      />

      {/* File preview renders as overlay via nested route */}
      <Outlet />
    </div>
  );
};

// ============================================================================
// Tree View Component
// ============================================================================

interface TreeViewProps {
  nodes: TreeNode[];
  currentFolderId: string | null;
  onNavigate: (folderId: string | null) => void;
  level?: number;
}

const TreeView: React.FC<TreeViewProps> = ({ nodes, currentFolderId, onNavigate, level = 0 }) => {
  const { t } = useTranslation('files');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  if (nodes.length === 0 && level === 0) {
    return (
      <button
        onClick={() => onNavigate(null)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg ${
          currentFolderId === null ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <span>{t('breadcrumbs.all_disk')}</span>
      </button>
    );
  }

  return (
    <div className="space-y-0.5" style={{ marginLeft: level > 0 ? 12 : 0 }}>
      {level === 0 && (
        <button
          onClick={() => onNavigate(null)}
          className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg ${
            currentFolderId === null ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <span>{t('breadcrumbs.all_disk')}</span>
        </button>
      )}
      {nodes.map(node => {
        const hasChildren = node.children.length > 0;
        const isExpanded = expandedIds.has(node.node.id);
        const isSelected = currentFolderId === node.node.id;

        return (
          <div key={node.node.id}>
            <button
              onClick={() => onNavigate(node.node.id)}
              className={`w-full flex items-center gap-1 px-2 py-1.5 text-sm rounded-lg ${
                isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {hasChildren ? (
                <span
                  onClick={(e) => toggleExpand(node.node.id, e)}
                  className="p-0.5 hover:bg-gray-200 rounded"
                >
                  <svg
                    className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              ) : (
                <span className="w-4" />
              )}
              <span className="text-base">{FILE_TYPE_CONFIG.folder.icon}</span>
              <span className="truncate">{node.node.name}</span>
            </button>
            {hasChildren && isExpanded && (
              <TreeView
                nodes={node.children}
                currentFolderId={currentFolderId}
                onNavigate={onNavigate}
                level={level + 1}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// File Thumbnail Component
// ============================================================================

interface FileThumbnailProps {
  fileId: string;
  fallbackIcon: string;
  fallbackColor: string;
  size?: 'sm' | 'md' | 'lg';
}

const FileThumbnail: React.FC<FileThumbnailProps> = ({
  fileId,
  fallbackIcon,
  fallbackColor,
  size = 'sm',
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
  };

  // Fetch the presigned URL from API with auth
  useEffect(() => {
    let cancelled = false;
    const fetchPreviewUrl = async () => {
      try {
        const response = await getPreviewUrl(fileId, size);
        if (!cancelled && response?.preview_url) {
          setPreviewUrl(response.preview_url);
        }
      } catch {
        if (!cancelled) {
          setImageError(true);
        }
      }
    };
    fetchPreviewUrl();
    return () => { cancelled = true; };
  }, [fileId, size]);

  if (imageError || !previewUrl) {
    return <span className={fallbackColor}>{fallbackIcon}</span>;
  }

  return (
    <div className={`${sizeClasses[size]} mx-auto relative`}>
      {!imageLoaded && (
        <span className={`absolute inset-0 flex items-center justify-center ${fallbackColor}`}>
          {fallbackIcon}
        </span>
      )}
      <img
        src={previewUrl}
        alt=""
        className={`${sizeClasses[size]} object-cover rounded transition-opacity ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
      />
    </div>
  );
};

// ============================================================================
// List Thumbnail Component (smaller for list view)
// ============================================================================

interface ListThumbnailProps {
  fileId: string;
  fallbackIcon: string;
  fallbackColor: string;
}

const ListThumbnail: React.FC<ListThumbnailProps> = ({
  fileId,
  fallbackIcon,
  fallbackColor,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Fetch the presigned URL from API with auth
  useEffect(() => {
    let cancelled = false;
    const fetchPreviewUrl = async () => {
      try {
        const response = await getPreviewUrl(fileId, 'sm');
        if (!cancelled && response?.preview_url) {
          setPreviewUrl(response.preview_url);
        }
      } catch {
        if (!cancelled) {
          setImageError(true);
        }
      }
    };
    fetchPreviewUrl();
    return () => { cancelled = true; };
  }, [fileId]);

  if (imageError || !previewUrl) {
    return <span className={`text-xl ${fallbackColor}`}>{fallbackIcon}</span>;
  }

  return (
    <div className="w-6 h-6 relative flex-shrink-0">
      {!imageLoaded && (
        <span className={`absolute inset-0 flex items-center justify-center text-xl ${fallbackColor}`}>
          {fallbackIcon}
        </span>
      )}
      <img
        src={previewUrl}
        alt=""
        className={`w-6 h-6 object-cover rounded transition-opacity ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
      />
    </div>
  );
};

// ============================================================================
// Grid View Component
// ============================================================================

interface FileViewProps {
  files: FileNode[];
  selectedIds: Set<string>;
  onFileClick: (file: FileNode) => void;
  onSelect: (fileId: string, event: React.MouseEvent) => void;
  onRename: (file: FileNode) => void;
  onDelete: (file: FileNode) => void;
  onDownload: (file: FileNode) => void;
  onMove: (file: FileNode) => void;
  onCopy: (file: FileNode) => void;
}

const GridView: React.FC<FileViewProps> = ({
  files,
  selectedIds,
  onFileClick,
  onSelect,
  onRename,
  onDelete,
  onDownload,
  onMove,
  onCopy,
}) => {
  const { t } = useTranslation('files');
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {files.map(file => {
        const config = getFileTypeConfig(file);
        const isSelected = selectedIds.has(file.id);

        return (
          <div
            key={file.id}
            onClick={() => onFileClick(file)}
            onContextMenu={(e) => { e.preventDefault(); onSelect(file.id, e); }}
            className={`group relative bg-white rounded-lg border-2 p-4 cursor-pointer transition-all hover:shadow-md ${
              isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {/* Selection checkbox */}
            <div
              className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => onSelect(file.id, e)}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}}
                className="w-4 h-4 rounded border-gray-300"
              />
            </div>

            {/* Actions menu */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <FileActionsMenu
                file={file}
                onRename={onRename}
                onDelete={onDelete}
                onDownload={onDownload}
                onMove={onMove}
                onCopy={onCopy}
              />
            </div>

            {/* Icon / Thumbnail */}
            <div className="text-4xl text-center mb-3">
              {!file.is_folder && isProcessableType(file.content_type) ? (
                <FileThumbnail
                  fileId={file.id}
                  fallbackIcon={config.icon}
                  fallbackColor={config.color}
                />
              ) : (
                <span className={config.color}>{config.icon}</span>
              )}
            </div>

            {/* Name */}
            <p className="text-sm text-center font-medium text-gray-900 truncate" title={file.name}>
              {file.name}
            </p>

            {/* Relevance Badge (shown only for search results) */}
            {file.relevance !== undefined && file.relevance !== null && (
              <div className="flex justify-center mt-1">
                <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                  file.relevance >= 0.8 ? 'bg-green-100 text-green-800' :
                  file.relevance >= 0.6 ? 'bg-lime-100 text-lime-800' :
                  file.relevance >= 0.4 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-orange-100 text-orange-800'
                }`}>
                  {Math.round(file.relevance * 100)}{t('list_view.match_percentage')}
                </span>
              </div>
            )}

            {/* Size */}
            {!file.is_folder && file.relevance === undefined && (
              <p className="text-xs text-center text-gray-500 mt-1">
                {formatFileSize(file.file_size)}
              </p>
            )}

            {/* Processing Status Badge */}
            {!file.is_folder && isProcessableType(file.content_type) && (
              <div className="mt-2 flex justify-center">
                <FileProcessingBadge
                  fileId={file.id}
                  contentType={file.content_type}
                  compact={true}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// List View Component
// ============================================================================

const ListView: React.FC<FileViewProps> = ({
  files,
  selectedIds,
  onFileClick,
  onSelect,
  onRename,
  onDelete,
  onDownload,
  onMove,
  onCopy,
}) => {
  const { t } = useTranslation('files');
  // Check if any files have relevance scores (i.e., search results)
  const hasRelevanceScores = files.some(f => f.relevance !== undefined && f.relevance !== null);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="w-8 px-4 py-3" />
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('list_view.column_name')}</th>
            {hasRelevanceScores && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">{t('list_view.column_relevance')}</th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">{t('list_view.column_size')}</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">{t('list_view.column_status')}</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">{t('list_view.column_modified')}</th>
            <th className="w-8 px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {files.map(file => {
            const config = getFileTypeConfig(file);
            const isSelected = selectedIds.has(file.id);

            return (
              <tr
                key={file.id}
                onClick={() => onFileClick(file)}
                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
              >
                <td className="px-4 py-3" onClick={(e) => onSelect(file.id, e)}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {!file.is_folder && isProcessableType(file.content_type) ? (
                      <ListThumbnail
                        fileId={file.id}
                        fallbackIcon={config.icon}
                        fallbackColor={config.color}
                      />
                    ) : (
                      <span className={`text-xl ${config.color}`}>{config.icon}</span>
                    )}
                    <span className="font-medium text-gray-900 truncate">{file.name}</span>
                  </div>
                </td>
                {hasRelevanceScores && (
                  <td className="px-4 py-3 text-sm hidden sm:table-cell">
                    {file.relevance !== undefined && file.relevance !== null ? (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        file.relevance >= 0.8 ? 'bg-green-100 text-green-800' :
                        file.relevance >= 0.6 ? 'bg-lime-100 text-lime-800' :
                        file.relevance >= 0.4 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {Math.round(file.relevance * 100)}%
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                  {file.is_folder ? '—' : formatFileSize(file.file_size)}
                </td>
                <td className="px-4 py-3 text-sm hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                  {!file.is_folder && isProcessableType(file.content_type) ? (
                    <FileProcessingBadge
                      fileId={file.id}
                      contentType={file.content_type}
                      compact={true}
                    />
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                  {formatFileDate(file.updated_at)}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <FileActionsMenu
                    file={file}
                    onRename={onRename}
                    onDelete={onDelete}
                    onDownload={onDownload}
                    onMove={onMove}
                    onCopy={onCopy}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// File Actions Menu
// ============================================================================

interface FileActionsMenuProps {
  file: FileNode;
  onRename: (file: FileNode) => void;
  onDelete: (file: FileNode) => void;
  onDownload: (file: FileNode) => void;
  onMove: (file: FileNode) => void;
  onCopy: (file: FileNode) => void;
}

const FileActionsMenu: React.FC<FileActionsMenuProps> = ({ file, onRename, onDelete, onDownload, onMove, onCopy }) => {
  const { t } = useTranslation('files');
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if this is a protected system folder
  const isProtected = isSystemFolder(file);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className="p-1 hover:bg-gray-200 rounded"
      >
        <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {!file.is_folder && (
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(file); setIsOpen(false); }}
              className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {t('actions.download')}
            </button>
          )}
          {/* Rename disabled for system folders */}
          {!isProtected && (
            <button
              onClick={(e) => { e.stopPropagation(); onRename(file); setIsOpen(false); }}
              className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t('actions.rename')}
            </button>
          )}
          {/* Move disabled for system folders */}
          {!isProtected && (
            <button
              onClick={(e) => { e.stopPropagation(); onMove(file); setIsOpen(false); }}
              className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              {t('actions.move_to')}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onCopy(file); setIsOpen(false); }}
            className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {t('actions.copy_to')}
          </button>
          {/* Delete disabled for system folders */}
          {!isProtected && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(file); setIsOpen(false); }}
              className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {t('actions.delete')}
            </button>
          )}
          {/* Show protected indicator for system folders */}
          {isProtected && (
            <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-2 border-t border-gray-100 mt-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              {t('actions.system_folder')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Empty State Component
// ============================================================================

interface EmptyStateProps {
  isSearch: boolean;
  onCreateFolder: () => void;
  onUpload: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ isSearch, onCreateFolder, onUpload }) => {
  const { t } = useTranslation('files');
  return (
    <div className="text-center py-12">
      <div className="text-6xl mb-4">{isSearch ? '🔍' : '📂'}</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {isSearch ? t('empty_state.no_files_found') : t('empty_state.folder_empty')}
      </h3>
      <p className="text-gray-500 mb-6">
        {isSearch
          ? t('empty_state.try_search')
          : t('empty_state.drag_drop')}
      </p>
      {!isSearch && (
        <div className="flex gap-3 justify-center">
          <button onClick={onCreateFolder} className="btn-secondary">
            {t('empty_state.button_create_folder')}
          </button>
          <button onClick={onUpload} className="btn-primary">
            {t('empty_state.button_upload_files')}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Modal Component
// ============================================================================

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold">{title}</h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {children}
    </div>
  </div>
);

export default FilesModule;
